import type { FunctionLanguage } from '../../../../../../api/opmApi';

// ============================================================
// 内部辅助函数
// ============================================================

/** 将 inputSchema / outputSchema 统一规范为数组形式 */
function normalizeSchemaToItems(schema: any): any[] {
  if (!schema) return [];
  if (typeof schema === 'string') {
    try {
      schema = JSON.parse(schema);
    } catch {
      return [];
    }
  }
  if (Array.isArray(schema)) return schema;
  if (schema.type === 'object' || schema.properties) return [schema];
  return [];
}

/** 取 item 的 codeName 作为字段名/参数名 */
function getItemCodeName(item: any, fallback: string): string {
  return item.codeName || item.name || fallback;
}

/** 取 item 的接口名/类名（首字母大写） */
function getItemInterfaceName(item: any, fallback: string): string {
  const base =
    item.codeName || item.ontologyObjectName || item.name || fallback;
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/** 判断 item 是否为对象类型（需要生成 interface / TypedDict） */
function isObjectItem(item: any): boolean {
  return !!(item.properties && typeof item.properties === 'object');
}

// ============================================================
// TypeScript 代码生成
// ============================================================

function primitiveToTsType(type: string): string {
  switch (type) {
    case 'string':
      return 'string';
    case 'number':
      return 'number';
    case 'integer':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array':
      return 'any[]';
    case 'object':
      return 'Record<string, any>';
    default:
      return 'any';
  }
}

/** 生成单个 TypeScript interface（含 properties 展开） */
function buildTsInterface(interfaceName: string, item: any): string[] {
  const lines: string[] = [];
  lines.push(`interface ${interfaceName} {`);
  if (item.properties && typeof item.properties === 'object') {
    for (const [propKey, propDef] of Object.entries(item.properties) as [
      string,
      any,
    ][]) {
      const fieldName = propDef.codeName || propKey;
      const enumValues = propDef.enum || propDef.enumValues;
      let tsType: string;
      if (enumValues && Array.isArray(enumValues) && enumValues.length > 0) {
        const isNumberType =
          propDef.type === 'number' || propDef.type === 'integer';
        tsType = enumValues
          .map((value: any) => (isNumberType ? String(value) : `'${value}'`))
          .join(' | ');
      } else {
        tsType = primitiveToTsType(propDef.type || 'string');
      }
      if (propDef.isArray) tsType = `${tsType}[]`;
      const optional = propDef.required === true ? '' : '?';
      const comment =
        propDef.name && propDef.name !== fieldName ? ` // ${propDef.name}` : '';
      lines.push(`  ${fieldName}${optional}: ${tsType};${comment}`);
    }
  }
  lines.push('}');
  return lines;
}

/**
 * 生成 TypeScript 完整签名代码：
 * 各入参/出参独立 interface → FunctionRequest wrapper → FunctionResponse wrapper → 函数声明
 */
function generateTypeScriptSignature(
  functionNameText: string,
  inputItems: any[],
  outputItems: any[]
): string {
  const lines: string[] = [];

  // 各入参独立接口定义（只对对象类型生成 interface，基础类型直接使用原始类型）
  inputItems.forEach((item, index) => {
    if (isObjectItem(item)) {
      const interfaceName = getItemInterfaceName(item, `Input${index}`);
      lines.push(...buildTsInterface(interfaceName, item));
      lines.push('');
    }
  });

  // 各出参独立接口定义（只对对象类型生成 interface，基础类型直接使用原始类型）
  outputItems.forEach((item, index) => {
    if (isObjectItem(item)) {
      const interfaceName = getItemInterfaceName(item, `Output${index}`);
      lines.push(...buildTsInterface(interfaceName, item));
      lines.push('');
    }
  });

  // FunctionRequest wrapper（汇总所有入参）
  lines.push('interface FunctionRequest {');
  inputItems.forEach((item, index) => {
    const fieldName = getItemCodeName(item, `input${index}`);
    const optional = item.required === true ? '' : '?';
    const arraySuffix = item.isArray ? '[]' : '';
    // 对象类型引用接口名，基础类型直接用原始 TS 类型
    const tsType = isObjectItem(item)
      ? getItemInterfaceName(item, `Input${index}`)
      : primitiveToTsType(item.type || 'string');
    lines.push(`  ${fieldName}${optional}: ${tsType}${arraySuffix};`);
  });
  lines.push('}');
  lines.push('');

  // FunctionResponse wrapper（汇总所有出参）
  lines.push('interface FunctionResponse {');
  outputItems.forEach((item, index) => {
    const fieldName = getItemCodeName(item, `output${index}`);
    const arraySuffix = item.isArray ? '[]' : '';
    // 对象类型引用接口名，基础类型直接用原始 TS 类型
    const tsType = isObjectItem(item)
      ? getItemInterfaceName(item, `Output${index}`)
      : primitiveToTsType(item.type || 'string');
    lines.push(`  ${fieldName}: ${tsType}${arraySuffix};`);
  });
  lines.push('}');
  lines.push('');

  // 函数签名：统一用 FunctionRequest / FunctionResponse
  lines.push(
    `function ${functionNameText}(request: FunctionRequest): FunctionResponse {`
  );
  lines.push('  // TODO: implement');
  lines.push('}');

  return lines.join('\n');
}

// ============================================================
// Python 代码生成
// ============================================================

function primitiveToPyType(type: string): string {
  switch (type) {
    case 'string':
      return 'str';
    case 'number':
      return 'float';
    case 'integer':
      return 'int';
    case 'boolean':
      return 'bool';
    case 'array':
      return 'list';
    case 'object':
      return 'dict';
    default:
      return 'Any';
  }
}

/** 生成单个 Python TypedDict class（含 properties 展开） */
function buildPyTypedDict(className: string, item: any): string[] {
  const lines: string[] = [];
  lines.push(`class ${className}(TypedDict, total=False):`);
  const entries =
    item.properties && typeof item.properties === 'object'
      ? (Object.entries(item.properties) as [string, any][])
      : [];
  if (entries.length > 0) {
    for (const [propKey, propDef] of entries) {
      // 字段名直接取 codeName，保持与 schema 一致
      const fieldName = propDef.codeName || propKey;
      const baseType = primitiveToPyType(propDef.type || 'string');
      const pyType = propDef.isArray ? `List[${baseType}]` : baseType;
      const comment =
        propDef.name && propDef.name !== (propDef.codeName || propKey)
          ? `  # ${propDef.name}`
          : '';
      lines.push(`    ${fieldName}: ${pyType}${comment}`);
    }
  } else {
    // 空类体需要 pass，否则 Python 语法错误
    lines.push('    pass');
  }
  return lines;
}

/**
 * 生成 Python 完整签名代码：
 * 各入参/出参独立 TypedDict class → FunctionRequest wrapper → FunctionResponse wrapper → 函数声明
 */
function generatePythonSignature(
  functionNameText: string,
  inputItems: any[],
  outputItems: any[]
): string {
  const lines: string[] = [];
  lines.push('from typing import Any, List, Optional');
  lines.push('from typing_extensions import TypedDict');
  lines.push('');

  // 各入参独立 TypedDict class（只对对象类型生成，基础类型直接使用原始类型）
  inputItems.forEach((item, index) => {
    if (isObjectItem(item)) {
      const className = getItemInterfaceName(item, `Input${index}`);
      lines.push(...buildPyTypedDict(className, item));
      lines.push('');
    }
  });

  // 各出参独立 TypedDict class（只对对象类型生成，基础类型直接使用原始类型）
  outputItems.forEach((item, index) => {
    if (isObjectItem(item)) {
      const className = getItemInterfaceName(item, `Output${index}`);
      lines.push(...buildPyTypedDict(className, item));
      lines.push('');
    }
  });

  // FunctionRequest wrapper（汇总所有入参，total=False 表示字段均可选）
  lines.push('class FunctionRequest(TypedDict, total=False):');
  if (inputItems.length > 0) {
    inputItems.forEach((item, index) => {
      // 参数名直接取 codeName，不做 snake_case 转换
      const fieldName = getItemCodeName(item, `input${index}`);
      // 对象类型引用 TypedDict 类名，基础类型直接用 Python 原始类型
      const pyType = isObjectItem(item)
        ? getItemInterfaceName(item, `Input${index}`)
        : primitiveToPyType(item.type || 'string');
      lines.push(`    ${fieldName}: ${pyType}`);
    });
  } else {
    lines.push('    pass');
  }
  lines.push('');

  // FunctionResponse wrapper（汇总所有出参）
  lines.push('class FunctionResponse(TypedDict, total=False):');
  if (outputItems.length > 0) {
    outputItems.forEach((item, index) => {
      const fieldName = getItemCodeName(item, `output${index}`);
      // 对象类型引用 TypedDict 类名，基础类型直接用 Python 原始类型
      const pyType = isObjectItem(item)
        ? getItemInterfaceName(item, `Output${index}`)
        : primitiveToPyType(item.type || 'string');
      lines.push(`    ${fieldName}: ${pyType}`);
    });
  } else {
    lines.push('    pass');
  }
  lines.push('');

  // 函数签名：统一用 FunctionRequest / FunctionResponse
  lines.push(
    `def ${functionNameText}(request: FunctionRequest) -> FunctionResponse:`
  );
  lines.push('    # TODO: implement');
  lines.push('    pass');

  return lines.join('\n');
}

// ============================================================
// 已保存代码的函数体提取与合并
// ============================================================

/**
 * 从已保存的旧代码中提取用户编写的函数体，与新生成的签名头部合并。
 * 接口定义可能随时变更，每次加载都重新生成头部（包含最新接口定义），
 * 仅保留函数声明行之后用户编写的逻辑，保证代码与 schema 同步。
 */
export function mergeSignatureWithSavedBody(opts: {
  newSignature: string;
  savedCode: string;
  language: FunctionLanguage;
}): string {
  const { newSignature, savedCode, language } = opts;

  if (!savedCode || !savedCode.trim()) return newSignature;

  try {
    if (language === 'PYTHON') {
      return mergePythonBody(newSignature, savedCode);
    }
    return mergeTsBody(newSignature, savedCode);
  } catch {
    // 提取失败时安全降级，使用新签名
    return newSignature;
  }
}

/** TypeScript：用新签名头部替换旧代码头部，保留函数体 */
function mergeTsBody(newSignature: string, savedCode: string): string {
  const savedLines = savedCode.split('\n');

  // 在旧代码中定位函数声明行（以 function 关键字开头）
  const funcDeclIdx = savedLines.findIndex(line =>
    /^function\s+\w+/.test(line.trim())
  );
  if (funcDeclIdx === -1) return newSignature;

  // 函数声明行之后的内容（用户实际逻辑 + 结束括号）
  const afterFuncDecl = savedLines.slice(funcDeclIdx + 1);

  // 从尾部找到最后一个独立的 } 作为函数结束括号并排除它
  let lastBraceIdx = -1;
  for (let i = afterFuncDecl.length - 1; i >= 0; i--) {
    if (afterFuncDecl[i].trim() === '}') {
      lastBraceIdx = i;
      break;
    }
  }
  const bodyLines =
    lastBraceIdx >= 0 ? afterFuncDecl.slice(0, lastBraceIdx) : afterFuncDecl;

  // 在新签名中定位函数声明行，取其之前的头部内容
  const newLines = newSignature.split('\n');
  const newFuncDeclIdx = newLines.findIndex(line =>
    /^function\s+\w+/.test(line.trim())
  );
  if (newFuncDeclIdx === -1) return newSignature;

  const newHeader = newLines.slice(0, newFuncDeclIdx + 1);

  return [...newHeader, ...bodyLines, '}'].join('\n');
}

/** Python：用新签名头部替换旧代码头部，保留函数体 */
function mergePythonBody(newSignature: string, savedCode: string): string {
  const savedLines = savedCode.split('\n');

  // 在旧代码中定位函数声明行（以 def 关键字开头）
  const funcDeclIdx = savedLines.findIndex(line =>
    /^def\s+\w+/.test(line.trim())
  );
  if (funcDeclIdx === -1) return newSignature;

  // 函数声明行之后的内容即为函数体（含缩进）
  const bodyLines = savedLines.slice(funcDeclIdx + 1);

  // 在新签名中定位函数声明行，取其之前的头部内容
  const newLines = newSignature.split('\n');
  const newFuncDeclIdx = newLines.findIndex(line =>
    /^def\s+\w+/.test(line.trim())
  );
  if (newFuncDeclIdx === -1) return newSignature;

  const newHeader = newLines.slice(0, newFuncDeclIdx + 1);

  return [...newHeader, ...bodyLines].join('\n');
}

// ============================================================
// 公共导出
// ============================================================

/**
 * 根据函数名、输入输出 Schema 和语言生成函数签名代码。
 * 生成结构：各参数独立接口/类 → FunctionRequest → FunctionResponse → 函数声明
 */
export function generateFunctionSignature(opts: {
  functionName?: string;
  inputSchema?: any;
  outputSchema?: any;
  language: FunctionLanguage;
}): string {
  const { functionName, inputSchema, outputSchema, language } = opts;
  const functionNameText = functionName || 'processFunction';
  const inputItems = normalizeSchemaToItems(inputSchema);
  const outputItems = normalizeSchemaToItems(outputSchema);

  if (language === 'PYTHON') {
    return generatePythonSignature(functionNameText, inputItems, outputItems);
  }

  return generateTypeScriptSignature(functionNameText, inputItems, outputItems);
}

/**
 * 根据后端返回的 inputSchema/outputSchema 生成 Monaco .d.ts 类型声明字符串。
 * 供 Monaco 编辑器内联类型提示使用，结构与实际生成代码一致。
 */
export function generateTypeDefsFromDefinition(opts: {
  functionName?: string;
  inputSchema?: any;
  outputSchema?: any;
  processName?: string;
}): string {
  const { functionName, inputSchema, outputSchema, processName } = opts;
  const lines: string[] = [];

  lines.push('// Auto-generated type definitions for Process function');
  lines.push(`// Process: ${processName || 'unknown'}`);
  lines.push('');

  const inputItems = normalizeSchemaToItems(inputSchema);
  const outputItems = normalizeSchemaToItems(outputSchema);

  if (inputItems.length > 0) {
    lines.push('// ===== 输入参数类型 =====');
    inputItems.forEach((item, index) => {
      const interfaceName = getItemInterfaceName(item, `Input${index}`);
      lines.push(...buildTsInterface(interfaceName, item));
      lines.push('');
    });
  }

  if (outputItems.length > 0) {
    lines.push('// ===== 输出参数类型 =====');
    outputItems.forEach((item, index) => {
      const interfaceName = getItemInterfaceName(item, `Output${index}`);
      lines.push(...buildTsInterface(interfaceName, item));
      lines.push('');
    });
  }

  // FunctionRequest 声明
  lines.push('interface FunctionRequest {');
  inputItems.forEach((item, index) => {
    const fieldName = getItemCodeName(item, `input${index}`);
    const typeName = getItemInterfaceName(item, `Input${index}`);
    const optional = item.required === true ? '' : '?';
    const arraySuffix = item.isArray ? '[]' : '';
    lines.push(`  ${fieldName}${optional}: ${typeName}${arraySuffix};`);
  });
  lines.push('}');
  lines.push('');

  // FunctionResponse 声明
  lines.push('interface FunctionResponse {');
  outputItems.forEach((item, index) => {
    const fieldName = getItemCodeName(item, `output${index}`);
    const typeName = getItemInterfaceName(item, `Output${index}`);
    const arraySuffix = item.isArray ? '[]' : '';
    lines.push(`  ${fieldName}: ${typeName}${arraySuffix};`);
  });
  lines.push('}');
  lines.push('');

  // 函数签名声明
  const functionNameText = functionName || 'processFunction';
  lines.push('// ===== 函数签名 =====');
  lines.push(
    `declare function ${functionNameText}(request: FunctionRequest): FunctionResponse;`
  );
  lines.push('');

  return lines.join('\n');
}
